import type { CSSProperties } from 'react';

export type PolicySource = 'customized' | 'inherited' | 'synthesized' | 'awaiting-first-review';

interface Props {
  source: PolicySource;
  /** Optional ISO timestamp of the last DV review on this org/repo. */
  lastReviewedAt?: string;
  /** Compact mode: just the badge, no timestamp. Used in tight rows. */
  compact?: boolean;
}

const palette: Record<PolicySource, { bg: string; fg: string; border: string; label: string; hint: string }> = {
  customized: {
    bg: 'var(--loop-green-100)',
    fg: 'var(--loop-green-700)',
    border: 'var(--loop-green-500)',
    label: 'customized',
    hint: 'Explicit entry in policies/central.yml',
  },
  inherited: {
    bg: 'var(--loop-neutral-100)',
    fg: 'var(--loop-neutral-700)',
    border: 'var(--loop-neutral-300)',
    label: 'inherits org default',
    hint: 'No per-repo override; uses the org defaults from central.yml',
  },
  synthesized: {
    bg: '#fef3c7',
    fg: '#92400e',
    border: '#fbbf24',
    label: 'synthesized — needs policy audit',
    hint: 'No central.yml entry; running on safe defaults (MEDIUM floor, general council, no auto-approve)',
  },
  'awaiting-first-review': {
    bg: 'var(--loop-neutral-100)',
    fg: 'var(--loop-neutral-600)',
    border: 'var(--loop-neutral-300)',
    label: 'awaiting first review',
    hint: 'Opted in, but Diff Vader has not run yet — policy source unknown until first review',
  },
};

const badgeStyle = (source: PolicySource): CSSProperties => {
  const p = palette[source];
  return {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: '999px',
    fontSize: '0.7rem',
    fontWeight: 528,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    whiteSpace: 'nowrap',
  };
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function PolicySourceBadge({ source, lastReviewedAt, compact }: Props) {
  const p = palette[source];
  return (
    <span
      title={p.hint}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--loop-space-100)' }}
    >
      <span style={badgeStyle(source)}>{p.label}</span>
      {!compact && lastReviewedAt && (
        <span style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>
          last reviewed {relativeTime(lastReviewedAt)}
        </span>
      )}
    </span>
  );
}
